/**
 * SP-8387 — human-readable value summaries for the automated-param "resulting
 * logic" preview. Turns the raw selected value of each parameter into a short
 * string (resolved labels, ranges, phrases, handles) per parameter type.
 */
import { getIdsOptions, baseParamOf } from './options';
import type {
    LogicalOperator,
    PreviewCondition,
    PreviewNode,
} from './AndOrLogic';

type LabelMap = Record<string, string>;
export type LabelMaps = Record<string, LabelMap>;

/** Build per-parameter id→label maps (incl. nested children + the platform list
 *  used by account handles) from the same options source the editor uses. */
export const buildLabelMaps = ({
    filters,
    client,
    entityType,
}: {
    filters: any;
    client: any;
    entityType: string;
}): LabelMaps => {
    let options: Record<string, any> = {};
    try {
        options = getIdsOptions({ filters, client, type: entityType }) || {};
    } catch {
        return {};
    }

    const maps: LabelMaps = {};
    Object.entries(options).forEach(([param, opts]) => {
        const arr =
            param === 'accountUsernames'
                ? opts?.platforms || []
                : Array.isArray(opts)
                  ? opts
                  : [];
        const map: LabelMap = {};
        arr.forEach((o: any) => {
            if (o == null || o.value == null) return;
            map[String(o.value)] = o.label ?? String(o.value);
            (o.nested || []).forEach((n: any) => {
                if (n == null || n.value == null) return;
                map[`${o.value}-${n.value}`] = n.label ?? String(n.value);
            });
        });
        maps[param] = map;
    });
    return maps;
};

const truthyKeys = (v: any): string[] => {
    if (Array.isArray(v)) return v.map(String);
    if (v && typeof v === 'object')
        return Object.keys(v)
            .filter(k => v[k])
            .map(String);
    return [];
};

const joinList = (
    items: string[],
    { quote = false, max = 4 }: { quote?: boolean; max?: number } = {},
): string => {
    const arr = items.filter(x => x != null && x !== '');
    if (!arr.length) return '';
    const shown = arr.slice(0, max).map(x => (quote ? `"${x}"` : x));
    const extra = arr.length - shown.length;
    return shown.join(', ') + (extra > 0 ? ` +${extra} more` : '');
};

const has = (x: any): boolean => x !== undefined && x !== null && x !== '';

const fmtNumber = (x: any): string => {
    const n = Number(x);
    return Number.isNaN(n) ? String(x) : n.toLocaleString();
};

const fmtDate = (x: any): string => {
    const d = x instanceof Date ? x : new Date(x);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
};

/** A value summary split into its comparator `verb` ("is any of", "contains",
 *  "is between" …) and the human-readable `values` text. Keeping them apart lets
 *  the preview style the selected values distinctly from the connecting words. */
export type ValueSummary = { verb: string; values: string };

const EMPTY_SUMMARY: ValueSummary = { verb: '', values: '' };

const describeRange = (
    v: any,
    kind: 'date' | 'number' | 'float',
): ValueSummary => {
    if (!v || typeof v !== 'object') return EMPTY_SUMMARY;
    const fmt = kind === 'date' ? fmtDate : kind === 'float' ? String : fmtNumber;
    const start = has(v.start) ? fmt(v.start) : '';
    const end = has(v.end) ? fmt(v.end) : '';
    if (start && end) return { verb: 'is between', values: `${start} and ${end}` };
    if (start)
        return kind === 'date'
            ? { verb: 'is on or after', values: start }
            : { verb: 'is ≥', values: start };
    if (end)
        return kind === 'date'
            ? { verb: 'is on or before', values: end }
            : { verb: 'is ≤', values: end };
    return EMPTY_SUMMARY;
};

/** Short value summary for one parameter, formatted by its type. */
export const describeParamValue = (
    param: string,
    type: string,
    value: any,
    maps: LabelMaps,
): ValueSummary => {
    const map = maps[param] || {};
    switch (type) {
        case 'id':
        case 'compoundId': {
            const v = joinList(truthyKeys(value).map(k => map[k] ?? k));
            return v ? { verb: 'is any of', values: v } : EMPTY_SUMMARY;
        }
        case 'text': {
            const arr = Array.isArray(value) ? value : [];
            const prefix = param === 'hashtags' ? '#' : '';
            const v = joinList(
                arr.map(x => `${prefix}${x}`),
                { quote: param === 'postCaption' },
            );
            if (!v) return EMPTY_SUMMARY;
            return param === 'hashtags'
                ? { verb: 'includes', values: v }
                : { verb: 'contains', values: v };
        }
        case 'usernamesObject': {
            const arr = Array.isArray(value) ? value : [];
            const parts: string[] = [];
            arr.forEach((o: any) => {
                const platform =
                    o?.platformId != null ? map[String(o.platformId)] : '';
                (o?.names || []).forEach((n: string) =>
                    parts.push(platform ? `${platform} @${n}` : `@${n}`),
                );
            });
            const v = joinList(parts);
            return v ? { verb: 'includes', values: v } : EMPTY_SUMMARY;
        }
        case 'dateRange':
            return describeRange(value, 'date');
        case 'numberRange':
            return describeRange(value, 'number');
        case 'floatRange':
            return describeRange(value, 'float');
        default:
            return EMPTY_SUMMARY;
    }
};

/** A real (chosen) parameter row, as opposed to an `emptyParamN` placeholder. */
export const isRealParam = (param: string): boolean =>
    !!param && !param.startsWith('emptyParam');

/** Whether a selected-param entry actually holds any value (handles the boolean
 *  maps, arrays and range objects the different param types use). Must mirror the
 *  drawer's submit-time row filtering so the preview shows exactly the conditions
 *  that will be saved. */
export const paramHasValues = (selected: any): boolean => {
    if (!selected) return false;
    if (Array.isArray(selected))
        // Text rows are arrays of strings; account-handle rows are arrays of
        // {platformId, names} objects, and the submit path drops entries missing
        // either part — an entry only counts here once it would be saved.
        return selected.some(v =>
            v && typeof v === 'object'
                ? !!v.platformId &&
                  Array.isArray(v.names) &&
                  v.names.length > 0
                : has(v),
        );
    if (typeof selected === 'object') {
        // Range objects: 0 is a real bound ({start: 0} = "is ≥ 0"), so test
        // presence rather than truthiness.
        if ('start' in selected || 'end' in selected)
            return has(selected.start) || has(selected.end);
        return Object.values(selected).some(Boolean);
    }
    return !!selected;
};

/** Build the ordered, value-bearing conditions that drive the resulting-logic
 *  preview, in row order, each carrying the AND/OR operator joining it to the
 *  previous one and a human-readable value summary. Shared by the in-drawer
 *  builder and the pre-submit confirmation so both render identical logic. */
export const buildPreviewConditions = ({
    activeParams,
    allSelectedParams,
    logicalOperators,
    paramsOptions,
    labelMaps,
}: {
    activeParams: string[];
    allSelectedParams: Record<string, any> | undefined;
    logicalOperators: Record<string, LogicalOperator>;
    paramsOptions: { value: string; name?: string; type?: string }[];
    labelMaps: LabelMaps;
}): PreviewCondition[] =>
    activeParams
        .filter(
            p => isRealParam(p) && paramHasValues(allSelectedParams?.[p]),
        )
        .map((p, idx) => {
            // p is the row key ('sponsors' / 'sponsors#2'); resolve options and
            // value summary from the base parameter value (SP-8141).
            const base = baseParamOf(p);
            const opt = paramsOptions.find(o => o.value === base);
            const { verb, values } = describeParamValue(
                base,
                opt?.type,
                allSelectedParams?.[p],
                labelMaps,
            );
            return {
                label: opt?.name || base,
                verb,
                values,
                // Combined fallback string (verb + values) for any plain-text consumer.
                detail: [verb, values].filter(Boolean).join(' '),
                operator:
                    idx === 0
                        ? undefined
                        : logicalOperators[p] ?? ('AND' as const),
            };
        });

/** SP-8647: the grouped counterpart of {@link buildPreviewConditions}. Segments the
 *  value-bearing rows into top-level items (a bare condition, or a group of conditions
 *  combined by the group's single operator) so the preview can bracket groups. The
 *  operator on each item is the connector joining it to the previous top-level item. */
export const buildPreviewItems = ({
    activeParams,
    allSelectedParams,
    logicalOperators,
    groupAssignments,
    paramsOptions,
    labelMaps,
}: {
    activeParams: string[];
    allSelectedParams: Record<string, any> | undefined;
    logicalOperators: Record<string, LogicalOperator>;
    groupAssignments: Record<string, string>;
    paramsOptions: { value: string; name?: string; type?: string }[];
    labelMaps: LabelMaps;
}): PreviewNode[] => {
    const rows = activeParams.filter(isRealParam);

    const chipOf = (rowKey: string): PreviewCondition => {
        const base = baseParamOf(rowKey);
        const opt = paramsOptions.find(o => o.value === base);
        const { verb, values } = describeParamValue(
            base,
            opt?.type,
            allSelectedParams?.[rowKey],
            labelMaps,
        );
        return {
            label: opt?.name || base,
            verb,
            values,
            detail: [verb, values].filter(Boolean).join(' '),
        };
    };

    // Segment into top-level items BEFORE dropping value-less rows: consecutive rows
    // sharing a group id form a group, and each item keeps its TRUE first row — the
    // row whose operator is the item's top-level join, exactly as the builder stores
    // it and buildAutomatedParametersDocument serializes it. Filtering first would
    // let a still-empty leading row shift the join onto the next row's within-group
    // operator, previewing logic that differs from what gets saved.
    type Item = { groupId?: string; rowKeys: string[] };
    const items: Item[] = [];
    rows.forEach(rowKey => {
        const groupId = groupAssignments[rowKey];
        const last = items[items.length - 1];
        if (groupId && last && last.groupId === groupId) {
            last.rowKeys.push(rowKey);
        } else {
            items.push({ groupId, rowKeys: [rowKey] });
        }
    });

    const nodes: PreviewNode[] = [];
    items.forEach(item => {
        // Only value-bearing rows render; the item disappears when none do.
        const valueRows = item.rowKeys.filter(k =>
            paramHasValues(allSelectedParams?.[k]),
        );
        if (!valueRows.length) return;

        // The join to the previous item always reads from the item's true first
        // row (even while that row is still value-less); absent on the first
        // rendered item.
        const operator: LogicalOperator | undefined =
            nodes.length === 0
                ? undefined
                : logicalOperators[item.rowKeys[0]] ?? 'AND';

        // An ungrouped row — or a group with a single value-bearing row — reads
        // better as a bare condition (matches the serializer's collapse).
        if (!item.groupId || valueRows.length === 1) {
            nodes.push({
                kind: 'condition',
                operator,
                ...chipOf(valueRows[0]),
            });
            return;
        }
        nodes.push({
            kind: 'group',
            operator,
            // Each row inside the bracket keeps its own operator (null on the first).
            conditions: valueRows.map((rowKey, j) => ({
                ...chipOf(rowKey),
                operator:
                    j === 0
                        ? undefined
                        : logicalOperators[rowKey] ?? ('AND' as const),
            })),
        });
    });
    return nodes;
};
